import Observer from './Observer';

// types
//-----------------------------------------------------------------------------
export interface StateObserver {
  enter?: () => Promise<void>;
  leave?: () => Promise<void>;
}

export type ObserverMap<STATE extends string> = {
  [key in STATE]: StateObserver;
};

/*
  a state machine manager
*/
export default class StateMachine<State extends string> {
  // fields
  //---------------------------------------------------------------------------
  private _state: State | null = null;
  private _transitioning = false;
  private readonly _observers: ObserverMap<State>;
  private readonly _nullObserver: StateObserver = {};

  public onTransitioned = new Observer<{
    previousPhase: State | null;
    newPhase: State;
  }>();

  // properties
  //---------------------------------------------------------------------------
  public get state(): State {
    return this._state!;
  }

  public get observer(): StateObserver {
    return this._observers[this._state!] || this._nullObserver;
  }

  // init
  //---------------------------------------------------------------------------
  constructor(transitions: ObserverMap<State>) {
    // set fields
    this._observers = transitions;
  }

  // api
  //---------------------------------------------------------------------------
  public reset() {
    this._state = null;
    this._transitioning = false;
  }

  public async transition(state: State) {
    // if change state
    if (state !== this._state) {
      const oldObserver = this._observers[this._state!];
      const newObserver = this._observers[state];

      // set transitioning
      this._transitioning = true;

      // call leave
      await oldObserver?.leave?.();

      // update state
      this._state = state;

      // call enter
      await newObserver?.enter?.();

      // reset transitioning
      this._transitioning = false;

      // trigger event
      this.onTransitioned.publish({
        previousPhase: oldObserver ? this._state! : null,
        newPhase: this._state!,
      });
    }
  }
}
